import React, {
  useCallback,
  useEffect,
  useReducer
} from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';

import {
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import { Clear as IconClear } from '@material-ui/icons';

import {
  AutocompleteOption,
  ChecklistAutocomplete
} from '@checkit/react-components';

import { loaderReducer } from '../../common/delayedLoader';
import { EntitySnapshotWithType } from '../state';

import {
  RenderEntityAutocompleteContext,
  RenderEntityTableContext,
  SelectedEntityPanel
} from './SelectedEntityPanel';
import { messages } from './translations';

// TODO: move me to rules/state.ts
type EntitySnapshotForRule = EntitySnapshotWithType<'rule'>;

/** A component representing a single rule shown in the rules list.  */
const SelectedRulesTableItem = connect(
  (/* { checks }: State */) => ({ /* checks */ }) // TODO: get rules from state
)(function SelectedRulesTableItemImpl({ snapshot, isDeletable, handleDelete }: {
  /** The rule entity snapshot represented by this table item. */
  snapshot: EntitySnapshotForRule;
  /** Whether this rule can be removed from the current library. */
  isDeletable: boolean;
  /** Removes the rule corresponding to the given snapshot from the library's selected rules. */
  handleDelete: (snapshot: EntitySnapshotForRule) => void;
}) {
  const { formatMessage } = useIntl( );

  // TODO: fetch the rule's data by code once passed in

  const [ loadingState, loadingDispatch ] = useReducer(loaderReducer, {
    displayLoader: true,
    loading: false
  });

  useEffect(function simulateLoading( ) {
    loadingDispatch('loading');
    const timer = setTimeout(function simulateLoaded( ) {
      loadingDispatch('loaded');
    }, 1000);
    return ( ) => clearTimeout(timer);
  }, [ ]);

  /** Removes this rule from the library. */
  function onClickDelete( ) {
    handleDelete(snapshot);
  }

  return loadingState.displayLoader ? (
    <TableRow>
      <TableCell align="center" colSpan={4}>
        <CircularProgress color="inherit" size={12} />
      </TableCell>
    </TableRow>
  ) : (
    <TableRow>
      <TableCell component="th" scope="row">{snapshot.code}</TableCell>
      <TableCell padding="none">
        <IconButton
          aria-label="delete"
          size="small"
          onClick={onClickDelete}
          title={formatMessage(messages['rule.table.removeitem'])}
          disabled={!isDeletable}
        >
          <IconClear fontSize="inherit" />
        </IconButton>
      </TableCell>
    </TableRow>
  );
});

/** A table of rules selected by the user for inclusion in a library. */
function SelectedRulesTable({ isDeletable, rules, handleDelete }: {
  /** An array of rule names to display in the table. */
  rules: EntitySnapshotForRule[];
  /** A callback which handles the logic to decide whether a given rule can be removed by the user. */
  isDeletable: (rule: EntitySnapshotForRule) => boolean;
  /** Removes the rule corresponding to the given snapshot from the library's selected rules. */
  handleDelete: (snapshot: EntitySnapshotForRule) => void;
}) {
  const { formatMessage } = useIntl( );

  const items = rules.map((snapshot) => (
    <SelectedRulesTableItem
      key={snapshot.id}
      snapshot={snapshot}
      isDeletable={isDeletable(snapshot)}
      handleDelete={handleDelete}
    />
  ));

  return (
    <TableContainer component={Paper}>
      <Table size="small" aria-label={formatMessage(messages['rule.table.label'])}>
        <TableHead>
          <TableRow>
            <TableCell>{formatMessage(messages['rule.table.header.name'])}</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>{items}</TableBody>
      </Table>
    </TableContainer>
  );
}

/** An expandable form panel for selecting checks for inclusion in a library. */
export function SelectedRulesPanel({ selected, setSelected, isLibrarySaved, existing }: {
  /** The rules which have been selected. */
  selected: EntitySnapshotForRule[];
  /** A callback used to signal the fact that the selected rules have changed. */
  setSelected: (selection: EntitySnapshotForRule[]) => void;
  /** Whether the library these rules are in has actually been saved to the server. */
  isLibrarySaved: boolean;
  /** The rules which are already present in the server-persisted version of the library. */
  existing: EntitySnapshotForRule[];
}) {
  const { formatMessage } = useIntl( );
  const heading = formatMessage(messages['rule.form.heading']);

  const isDeletable = useCallback(function isRuleDeletable(rule) {
    return !existing.some((e) => e.code === rule.code);
  }, [ existing ]);

  const renderAutocomplete = ({ handleAdd }: RenderEntityAutocompleteContext<'rule'>) => {
    // TODO: implement logic to call handleAdd()
    const onAutocompleteOptionSelected = (
      _: React.ChangeEvent<{ }>,
      option: AutocompleteOption | null
    ) => null;
    // TODO: replace with a different autocomplete
    return (
      <ChecklistAutocomplete
        label={formatMessage(messages['check.table.header.name'])}
        onChange={onAutocompleteOptionSelected}
        fullWidth={true}
      />
    );
  };
  const renderTable = ({ handleDelete }: RenderEntityTableContext<'rule'>) => (
    <SelectedRulesTable rules={selected} isDeletable={isDeletable} handleDelete={handleDelete} />
  );

  return (
    <SelectedEntityPanel
      type="rule"
      heading={heading}

      selected={selected}
      setSelected={setSelected}
      isLibrarySaved={isLibrarySaved}

      Autocomplete={renderAutocomplete}
      Table={renderTable}
    />
  );
}
